import React, {FC, useCallback, useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import styled from 'styled-components';
import {useShowToast} from '../../../Toast/useShowToast';
import {HelpCenterEndpoint} from '../../../pages/HelpCenter/helpCenterEndpoint';
import {HelpCenterPageCommonProps} from '../../../pages/HelpCenter/types';
import {Locale} from '../../../types';
import {useHydrationDone} from '../../../utils/hooks/useHydrationDone';
import FileInput from '../../Forms/Inputs/FileInput';
import TextArea from '../../Forms/Inputs/TextArea';
import {mediaMinTablet} from '../../constants/customMedia';
import {fontSize16} from '../../constants/typography';
import {FullscreenLoading} from '../../utilityComponents/Loading/Loading';
import {getShippingDestinationList} from '../assets/shippingDestinationList';
import {
  useClearContactFormState,
  useContactFormState,
  useSaveContactFormState,
} from '../hooks/useContactFormState';
import {CountrySection} from './components/CountrySection';
import {EmailInputSection} from './components/EmailInputSection';
import {OrderRelatedSection} from './components/OrderRelatedSection';
import {SubjectSection} from './components/SubjectSection';
import {TopAnswersSection} from './components/TopAnswersSection';
import {UserDetailsSection} from './components/UserDetailsSection';
import {
  FormRowTwoEqualItems,
  FormSection,
  StyledForm,
  SubmitButton,
} from './components/styled';
import createContactFormSubmitHandler from './createContactFormSubmitHandler';
import {useTopAnswers} from './hooks/useTopAnswers';
import {useTrackFormSubmit} from './hooks/useTrackFormSubmit';
import {FormDataRaw} from './types';

const MAX_FILE_COUNT_CONTACT_FORM = 3;
const MAX_FILE_SIZE_IN_BYTE_CONTACT_FORM = 3 * 1024 * 1024;
const AUTHORIZED_DOMAINS_FOR_COUNTRY_DROPDOWN = [Locale.en_ZZ, Locale.es_ZZ];

export type CloudflareTurnstileType = React.FC<{
  onExpire?: VoidFunction;
  onSuccess: (captchaCode: string) => void;
  reloadOnChange?: number | object;
  className?: string;
  type?: 'default' | 'auth';
  uniqueKey: string;
}>;

interface Props {
  translations: HelpCenterPageCommonProps['translations']['contactPage']['form'];
  sections: HelpCenterPageCommonProps['sections'];
  sendContactForm: HelpCenterEndpoint['sendContactForm'];
  endpoints: HelpCenterPageCommonProps['endpoints'];
  isVip?: boolean;
  isProduction: boolean;
  localizedServiceEmailAddress: string;
  locale: Locale;
  CloudflareTurnstile?: CloudflareTurnstileType;
}

const initialFormState: FormDataRaw = {
  email: '',
  country: {
    label: null,
    value: '',
  },
  subject: null,
  message: '',
  attachments: [],
  orderNumber: [{value: ''}],
  isOrder: 'false',
  street: '',
  houseNumber: '',
  addressAdditional: '',
  zipCode: '',
  city: '',
  birthDate: '',
};

const ContactForm: FC<Props> = props => {
  const {CloudflareTurnstile} = props;
  const hydrationDone = useHydrationDone();
  const {handleSubmit, register, control, errors, reset, getValues} =
    useForm<FormDataRaw>({
      defaultValues: initialFormState,
      mode: 'onBlur',
    });
  const [formInstanceKey, setFormInstanceKey] = useState(0);
  const [captchaKey, setCaptchaKey] = useState<number | undefined>();
  const [hideCaptcha, setHideCaptcha] = useState(false);
  const [captchaCode, setCaptchaCode] = useState<string>();
  const [isOrder, setIsOrder] = useState(false);
  const showToast = useShowToast();

  const HAS_COUNTRY_SELECTOR = AUTHORIZED_DOMAINS_FOR_COUNTRY_DROPDOWN.includes(
    props.locale,
  );

  const resetFormState = useCallback(() => {
    reset();
    setFormInstanceKey(formInstanceKey + 1);
    setIsOrder(false);
    setFormPhase('INPUT');
  }, [formInstanceKey, reset]);

  const [formPhase, setFormPhase] = useState<
    'INPUT' | 'SUBMITTING' | 'SUCCESS' | 'ERROR'
  >('INPUT');

  const topAnswers = useTopAnswers({
    control,
    endpoints: props.endpoints,
    locale: props.locale,
  });

  const trackFormSubmit = useTrackFormSubmit();

  const onSubmitCallback = () => {
    setHideCaptcha(true);
  };

  const onSuccess = () => {
    setCaptchaKey((captchaKey ?? 0) + 1);
  };

  const onError = () => {
    setHideCaptcha(false);
  };

  const onSubmit = createContactFormSubmitHandler({
    isVip: props.isVip,
    sendContactForm: props.sendContactForm,
    attachCountryOfShipping: HAS_COUNTRY_SELECTOR,
    resetFormState,
    setFormPhase,
    localizedServiceEmailAddress: props.localizedServiceEmailAddress,
    trackFormSubmit,
    onError,
    onSuccess,
    captchaCode,
    onSubmit: onSubmitCallback,
  });

  useEffect(() => {
    if (formPhase === 'SUCCESS' || formPhase === 'ERROR') {
      showToast({
        'data-testid': 'hcContactFormToast',
        type: formPhase,
        onAutoClose: () => {
          // eslint-disable-next-line local-rules/set-state-in-use-effect
          setFormPhase('INPUT');
          setHideCaptcha(false);
        },
        content: (
          <ToastWrapper>
            <ToastHeading>
              {formPhase === 'SUCCESS'
                ? props.translations.feedback_success_heading
                : props.translations.feedback_error_text}
            </ToastHeading>
            <span>
              {props.isVip
                ? props.translations.feedback_success_text_vip
                : props.translations.feedback_success_text_normal}
            </span>
          </ToastWrapper>
        ),
      });
    }
  }, [formPhase]);

  const saveFormState = useSaveContactFormState();
  const previousFormState = useContactFormState();
  const clearFormState = useClearContactFormState();

  const handleFormState = () => {
    const {attachments, ...data} = getValues();
    saveFormState(data);
  };

  // only on first mount
  useEffect(() => {
    if (previousFormState) {
      reset(previousFormState);
      setIsOrder(previousFormState.isOrder === 'true');
    }
  }, [previousFormState, reset]);

  // if form is submitted clear the form data
  useEffect(() => {
    if (formPhase === 'SUBMITTING') {
      clearFormState();
    }
  }, [formPhase, clearFormState]);

  const onExpire = useCallback(() => {
    setCaptchaCode(undefined);
  }, []);

  if (!hydrationDone) {
    return null;
  }

  return (
    <StyledForm
      key={formInstanceKey}
      onSubmit={handleSubmit(onSubmit)}
      method="POST"
      data-testid="hcContactForm"
    >
      <FormRowTwoEqualItems>
        {HAS_COUNTRY_SELECTOR && (
          <CountrySection
            control={control}
            labels={props.translations}
            errors={errors}
            sections={getShippingDestinationList(props.locale)}
          />
        )}
        <EmailInputSection
          control={control}
          labels={props.translations}
          errors={errors}
          register={register}
        />
      </FormRowTwoEqualItems>

      <OrderRelatedSection
        register={register}
        control={control}
        isOrder={isOrder}
        onChange={(value: string) => setIsOrder(value === 'true')}
        labels={props.translations}
        errors={errors}
      />

      <UserDetailsSection
        isOrder={isOrder}
        control={control}
        labels={props.translations}
        register={register}
      />

      <SubjectSection
        control={control}
        labels={props.translations}
        errors={errors}
        sections={props.sections}
      />

      <TopAnswersSection
        topAnswers={topAnswers}
        subject={getValues('subject')}
        labels={props.translations}
        onBeforeSelect={handleFormState}
      />

      <FormSection short>
        <TextArea
          name="message"
          ref={register({
            required: props.translations.required,
          })}
          label={props.translations.message_label}
          rows={8}
          errorMessage={errors.message?.message}
          data-testid="hcMessage"
        />
      </FormSection>
      <FormSection>
        <Controller
          control={control}
          name="attachments"
          defaultValue={[]}
          render={({onChange}) => (
            <FileInput
              label={props.translations.add_file}
              subline={props.translations.file_input_explanation}
              accept="image/png, image/jpeg, application/pdf"
              onChange={onChange}
              maxFiles={MAX_FILE_COUNT_CONTACT_FORM}
              maxFileSizeInByte={MAX_FILE_SIZE_IN_BYTE_CONTACT_FORM}
              data-testid="hcFileUpload"
            />
          )}
        />
      </FormSection>
      {CloudflareTurnstile && !hideCaptcha && (
        <CaptchaWrapper>
          <CloudflareTurnstile
            onSuccess={setCaptchaCode}
            type="default"
            onExpire={onExpire}
            reloadOnChange={captchaKey}
            uniqueKey="helpCenterContactForm"
          />
        </CaptchaWrapper>
      )}
      <SubmitButton
        $addMarginTop={!hideCaptcha}
        type="submit"
        data-testid="hcFormSubmit"
        disabled={formPhase === 'SUBMITTING' || !captchaCode}
      >
        {props.translations.submit}
      </SubmitButton>
      {formPhase === 'SUBMITTING' && <FullscreenLoading />}
    </StyledForm>
  );
};

export default ContactForm;

const CaptchaWrapper = styled.div`
  display: flex;
  justify-content: center;

  @media ${mediaMinTablet} {
    justify-content: unset;
  }
`;

const ToastWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ToastHeading = styled.p`
  ${fontSize16};
  margin: 0;
`;
